import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import {
  Plus,
  Zap,
  ThumbsUp,
  Lock,
  Loader2,
  X,
  History,
  AlertTriangle,
  AlertOctagon,
  CheckCircle2,
  Clock,
  ChevronDown,
  ChevronUp,
  ShieldCheck,
  Archive,
} from 'lucide-react';
import { Button } from './index';
import { B2bProjectWizard } from './B2bProjectWizard';
import {
  getB2bProjecten,
  getMijlpaalUrenLogs,
  keurExtraUrenGoed,
  accordeerMijlpaal,
  type B2bProject,
  type ProjectMijlpaal,
  type B2bMilestoneStatus,
  type BudgetSignaal,
} from '../services/lopendeTrajectenApi';

/**
 * Tabel 3 — het B2B projectregie-grid (Long-term Project Engine & Budget-Grendel, 136).
 *
 * Zeven kolommen: Project & Professional · Actieve mijlpaal · Uren-teller (klikbaar
 * → slide-over met uren-logs) · Financieel verbruik · State-machine status ·
 * Budget-Grendel · Contextuele actieknop.
 */

export const QK_PROJECTEN = ['lopende-trajecten', 'projecten'];
export const QK_OVERZICHT = ['lopende-trajecten', 'overzicht'];

const euro = new Intl.NumberFormat('nl-NL', { style: 'currency', currency: 'EUR' });
const fmtEuro = (n: number | null | undefined) => euro.format(Number(n ?? 0));
const fmtUren = (n: number | null | undefined) => `${Number(n ?? 0).toLocaleString('nl-NL', { maximumFractionDigits: 2 })} u`;
const fmtDate = (iso: string | null | undefined) => (iso ? new Date(iso).toLocaleDateString('nl-NL') : '—');
const fmtDateTime = (iso: string | null | undefined) =>
  iso ? new Date(iso).toLocaleString('nl-NL', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';
const apiMessage = (err: any, fallback: string): string => err?.response?.data?.message || fallback;

const thClass = 'px-3 py-2 text-left text-xs font-semibold text-gray-600 uppercase tracking-wide whitespace-nowrap';
const tdClass = 'px-3 py-2 text-sm text-gray-800 align-top';

type Tone = 'green' | 'red' | 'blue' | 'orange' | 'gray' | 'yellow';
const TONE: Record<Tone, string> = {
  green: 'bg-green-100 text-green-800',
  red: 'bg-red-100 text-red-800',
  blue: 'bg-blue-100 text-blue-800',
  orange: 'bg-orange-100 text-orange-800',
  gray: 'bg-gray-100 text-gray-700',
  yellow: 'bg-yellow-100 text-yellow-800',
};
const Badge: React.FC<{ tone: Tone; children: React.ReactNode; mono?: boolean }> = ({ tone, children, mono }) => (
  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${TONE[tone]} ${mono ? 'font-mono' : ''}`}>{children}</span>
);

export const MILESTONE_STATUS: Record<B2bMilestoneStatus, { label: string; tone: Tone; icon: React.ComponentType<{ className?: string }> }> = {
  LOCKED_PENDING: { label: 'Vergrendeld', tone: 'gray', icon: Lock },
  ACTIVE_RUNNING: { label: 'Professional schrijft uren binnen de cap', tone: 'blue', icon: Clock },
  CAP_REACHED_PENDING_APPROVAL: { label: 'Uren-cap bereikt — registratie geblokkeerd', tone: 'orange', icon: AlertOctagon },
  COMPLETED_WAITING_FOR_FIAT: { label: 'Afgerond — wacht op uw fiat', tone: 'green', icon: CheckCircle2 },
  ARCHIVED: { label: 'Geaccordeerd & gearchiveerd', tone: 'gray', icon: Archive },
};

// ─── Uren-teller (Budgetmeter) ────────────────────────────────────────────────

const Budgetmeter: React.FC<{ m: ProjectMijlpaal; onClick?: () => void }> = ({ m, onClick }) => {
  const pct = m.percentage;
  const kleur = m.status === 'CAP_REACHED_PENDING_APPROVAL' || pct >= 100 ? 'bg-red-500' : pct >= 85 ? 'bg-orange-500' : 'bg-blue-600';
  return (
    <button type="button" onClick={onClick} className="group text-left w-full min-w-[160px]" title="Klik voor de live uren-logs (HR-audit)">
      <div className="flex items-center justify-between text-xs mb-1">
        <span className="font-mono">
          {m.geschreven_uren.toLocaleString('nl-NL', { maximumFractionDigits: 2 })} / {m.cap_uren ?? '—'} uur
        </span>
        <span className="text-gray-500">{pct}%</span>
      </div>
      <div className="h-2.5 w-full rounded-full bg-gray-200 overflow-hidden ring-offset-1 group-hover:ring-2 group-hover:ring-blue-300">
        <div className={`h-full ${kleur} transition-all`} style={{ width: `${Math.min(100, pct)}%` }} />
      </div>
      <div className="text-[11px] text-gray-500 mt-1 flex items-center gap-1 group-hover:text-blue-700">
        <History className="w-3 h-3" /> uren-logs
        {m.extra_uren_toegekend > 0 && <span className="ml-auto text-orange-700">+{m.extra_uren_toegekend} u extra</span>}
      </div>
    </button>
  );
};

// ─── Slide-over: live uren-logs ───────────────────────────────────────────────

const UrenSlideOver: React.FC<{ milestoneId: string; onSluit: () => void }> = ({ milestoneId, onSluit }) => {
  const { data, isLoading } = useQuery({ queryKey: [...QK_PROJECTEN, 'uren', milestoneId], queryFn: () => getMijlpaalUrenLogs(milestoneId) });
  const m = data?.mijlpaal;
  const totaalUren = data?.logs.reduce((s, l) => s + l.uren, 0) ?? 0;
  return (
    <div className="fixed inset-0 z-50 flex">
      <div className="flex-1 bg-black/30" onClick={onSluit} />
      <aside className="w-full max-w-lg bg-white shadow-2xl h-full flex flex-col">
        <div className="px-4 py-3 border-b border-gray-200 flex items-start gap-2">
          <History className="w-5 h-5 text-blue-600 mt-0.5" />
          <div className="flex-1 min-w-0">
            <div className="font-semibold text-gray-900">Live uren-logs (HR-audit)</div>
            {m && (
              <div className="text-xs text-gray-500 truncate">
                {m.project_code} · {m.project_titel} · Mijlpaal {m.volgnummer}: {m.titel}
              </div>
            )}
          </div>
          <button type="button" className="p-1 text-gray-400 hover:text-gray-700" onClick={onSluit} aria-label="Sluiten">
            <X className="w-5 h-5" />
          </button>
        </div>
        {m && (
          <div className="px-4 py-3 border-b border-gray-100 bg-gray-50">
            <Budgetmeter m={m} />
            <div className="grid grid-cols-3 gap-2 mt-3 text-xs">
              <div>
                <div className="text-gray-500">Verbruik</div>
                <div className="font-semibold">{fmtEuro(m.verbruik_bedrag)}</div>
              </div>
              <div>
                <div className="text-gray-500">Verzegeld maximum</div>
                <div className="font-semibold">{fmtEuro(m.budget_bedrag)}</div>
              </div>
              <div>
                <div className="text-gray-500">Resterend</div>
                <div className="font-semibold">{m.resterende_uren == null ? '—' : fmtUren(m.resterende_uren)}</div>
              </div>
            </div>
          </div>
        )}
        <div className="flex-1 overflow-y-auto">
          {isLoading ? (
            <div className="p-6 text-center text-gray-500 text-sm">
              <Loader2 className="w-5 h-5 animate-spin inline" />
            </div>
          ) : !data || data.logs.length === 0 ? (
            <div className="p-6 text-center text-gray-500 text-sm">Nog geen uren geregistreerd op deze mijlpaal.</div>
          ) : (
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 sticky top-0">
                <tr>
                  <th className={thClass}>Datum</th>
                  <th className={thClass}>Omschrijving</th>
                  <th className={`${thClass} text-right`}>Uren</th>
                  <th className={`${thClass} text-right`}>Bedrag</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {data.logs.map((l) => (
                  <tr key={l.id}>
                    <td className={`${tdClass} whitespace-nowrap`}>
                      {fmtDate(l.datum)}
                      {l.bevroren && <div className="text-[10px] text-gray-400">bevroren</div>}
                    </td>
                    <td className={tdClass}>
                      {l.omschrijving || <span className="text-gray-400">—</span>}
                      <div className="text-[10px] text-gray-400">geregistreerd {fmtDateTime(l.geregistreerd_op)}</div>
                    </td>
                    <td className={`${tdClass} text-right font-mono`}>{l.uren.toLocaleString('nl-NL', { maximumFractionDigits: 2 })}</td>
                    <td className={`${tdClass} text-right`}>{l.bedrag == null ? '—' : fmtEuro(l.bedrag)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="bg-gray-50">
                <tr>
                  <td className={`${tdClass} font-semibold`} colSpan={2}>
                    Totaal ({data.logs.length} regels)
                  </td>
                  <td className={`${tdClass} text-right font-mono font-semibold`}>{totaalUren.toLocaleString('nl-NL', { maximumFractionDigits: 2 })}</td>
                  <td className={`${tdClass} text-right font-semibold`}>{fmtEuro(m?.verbruik_bedrag)}</td>
                </tr>
              </tfoot>
            </table>
          )}
        </div>
      </aside>
    </div>
  );
};

// ─── Budget-Grendel signalen ──────────────────────────────────────────────────

const GRENDEL: Record<Exclude<BudgetSignaal, null>, { tone: Tone; icon: React.ComponentType<{ className?: string }>; tekst: (m: ProjectMijlpaal) => string }> = {
  CAP_BEREIKT: {
    tone: 'red',
    icon: AlertOctagon,
    tekst: (m) => `ENUM_SIGNAL_MILESTONE_CAP — cap van ${m.cap_uren} uur bereikt op ${fmtDateTime(m.cap_bereikt_op)}. Urenregistratie is softwarematig geblokkeerd.`,
  },
  WACHT_OP_FIAT: {
    tone: 'green',
    icon: CheckCircle2,
    tekst: (m) => `Afgerond op ${fmtDateTime(m.afgerond_op)} in ${m.geschreven_uren} van ${m.cap_uren} uur. Bij accordering wordt uitsluitend ${fmtEuro(m.verbruik_bedrag)} geïncasseerd.`,
  },
  BIJNA_CAP: {
    tone: 'orange',
    icon: AlertTriangle,
    tekst: (m) => `Nog ${m.resterende_uren} uur beschikbaar binnen de cap (${m.percentage}% verbruikt).`,
  },
  GEEN_UREN: {
    tone: 'gray',
    icon: Clock,
    tekst: () => 'Nog geen uren geschreven op deze fase.',
  },
};

// ─── Contextuele actieknop ────────────────────────────────────────────────────

const ActieKnop: React.FC<{ project: B2bProject }> = ({ project }) => {
  const qc = useQueryClient();
  const m = project.actieve_mijlpaal;
  const [extraOpen, setExtraOpen] = useState(false);
  const [extra, setExtra] = useState('5');
  const refresh = () => {
    qc.invalidateQueries({ queryKey: QK_PROJECTEN });
    qc.invalidateQueries({ queryKey: QK_OVERZICHT });
  };

  const extraM = useMutation({
    mutationFn: () => keurExtraUrenGoed(m!.id, parseFloat(extra)),
    onSuccess: (rij) => {
      toast.success(`${extra} extra uur toegekend — nieuwe cap ${rij.cap_uren} uur; de blokkade is opgeheven`);
      setExtraOpen(false);
      refresh();
    },
    onError: (e: any) => toast.error(apiMessage(e, 'Extra uren toekennen mislukt')),
  });
  const accordeerM = useMutation({
    mutationFn: () => accordeerMijlpaal(m!.id),
    onSuccess: (r) => {
      const factuur = r.factuur ? ` Verzamelfactuur ${r.factuur.invoiceNumber} aangemaakt.` : ' Facturatie volgt via de maandcron.';
      toast.success(
        (r.volgende
          ? `Mijlpaal geaccordeerd (${fmtEuro(r.split.professional)} professional + ${fmtEuro(r.split.platform_fee)} platformfee = ${fmtEuro(r.split.bruto)} excl. btw). Mijlpaal ${r.volgende.volgnummer} is nu ACTIVE_RUNNING.`
          : `Laatste mijlpaal geaccordeerd (${fmtEuro(r.split.bruto)} excl. btw) — project afgerond.`) + factuur,
        { duration: 8000 }
      );
      refresh();
    },
    onError: (e: any) => toast.error(apiMessage(e, 'Accorderen mislukt')),
  });

  if (project.status !== 'ACTIEF' || !m) {
    return <span className="text-xs text-gray-500">{project.status === 'AFGEROND' ? 'Alle fasen geaccordeerd' : '—'}</span>;
  }

  if (m.status === 'COMPLETED_WAITING_FOR_FIAT') {
    return (
      <Button
        size="sm"
        className="bg-green-600 hover:bg-green-700 border-green-600 whitespace-nowrap"
        disabled={accordeerM.isPending}
        onClick={() => {
          if (
            window.confirm(
              `Mijlpaal ${m.volgnummer} accorderen voor ${m.geschreven_uren} uur (${fmtEuro(m.verbruik_bedrag)})?\n\n` +
                `De werkelijk geschreven uren gaan de SEPA-verzamelfactuur in (100% naar de professional, plus 15% platformfee) en de volgende fase wordt ontgrendeld.`
            )
          )
            accordeerM.mutate();
        }}
      >
        {accordeerM.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <><ThumbsUp className="w-3.5 h-3.5 mr-1" /> Accordeer Mijlpaal &amp; Open Volgende Fase</>}
      </Button>
    );
  }

  const capBereikt = m.status === 'CAP_REACHED_PENDING_APPROVAL';
  return (
    <div className="flex flex-col gap-1 min-w-[190px]">
      {!extraOpen ? (
        <Button
          size="sm"
          variant={capBereikt ? 'primary' : 'outline'}
          className={capBereikt ? 'bg-orange-500 hover:bg-orange-600 border-orange-500 whitespace-nowrap' : 'whitespace-nowrap'}
          onClick={() => setExtraOpen(true)}
        >
          <Zap className="w-3.5 h-3.5 mr-1" /> Keur Extra Uren Goed
        </Button>
      ) : (
        <div className="p-2 rounded-lg border border-orange-200 bg-orange-50 space-y-2">
          <label className="block text-[11px] font-medium text-orange-900">Extra uren alloceren (cap {m.cap_uren} → {(m.cap_uren ?? 0) + (parseFloat(extra) || 0)} uur)</label>
          <div className="flex gap-1">
            <input type="number" min="0.5" step="0.5" className="w-20 px-2 py-1 border border-gray-300 rounded text-sm" value={extra} onChange={(e) => setExtra(e.target.value)} />
            <Button size="sm" className="bg-orange-500 hover:bg-orange-600 border-orange-500" disabled={extraM.isPending || !(parseFloat(extra) > 0)} onClick={() => extraM.mutate()}>
              {extraM.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Toekennen'}
            </Button>
            <Button size="sm" variant="outline" onClick={() => setExtraOpen(false)}>
              <X className="w-3.5 h-3.5" />
            </Button>
          </div>
          <div className="text-[10px] text-orange-800">Meerwaarde: {fmtEuro((parseFloat(extra) || 0) * (m.uurtarief ?? 0))} extra verzegeld budget.</div>
        </div>
      )}
      {!capBereikt && <span className="text-[11px] text-gray-500">Wacht op ‘Mijlpaal afronden’ door de professional.</span>}
    </div>
  );
};

// ─── Projectrij (7 kolommen) ──────────────────────────────────────────────────

const ProjectRij: React.FC<{ p: B2bProject; onLogs: (milestoneId: string) => void }> = ({ p, onLogs }) => {
  const [open, setOpen] = useState(false);
  const m = p.actieve_mijlpaal;
  const st = m ? MILESTONE_STATUS[m.status] : null;
  const grendel = p.signaal ? GRENDEL[p.signaal] : null;
  return (
    <>
      <tr className={`hover:bg-gray-50 ${p.status !== 'ACTIEF' ? 'opacity-70' : ''}`}>
        {/* 1. Project & Professional */}
        <td className={tdClass}>
          <button type="button" className="flex items-start gap-1 text-left" onClick={() => setOpen((v) => !v)} title="Alle mijlpalen tonen">
            {open ? <ChevronUp className="w-4 h-4 mt-0.5 text-gray-400" /> : <ChevronDown className="w-4 h-4 mt-0.5 text-gray-400" />}
            <div>
              <div className="font-medium text-gray-900">{p.titel}</div>
              <div className="text-xs text-gray-600">
                {p.professional.naam} · {p.professional.discipline_label}
              </div>
              <div className="text-[11px] text-gray-400 font-mono">
                {p.project_code} · {fmtDate(p.start_datum)}
                {p.verwachte_eind_datum ? ` → ${fmtDate(p.verwachte_eind_datum)}` : ''}
              </div>
            </div>
          </button>
        </td>
        {/* 2. Actieve mijlpaal */}
        <td className={tdClass}>
          {m ? (
            <>
              <div className="font-medium">
                Mijlpaal {m.volgnummer}: {m.titel}
              </div>
              <div className="text-xs text-gray-500">
                fase {p.totaal.afgerond + 1} van {p.totaal.mijlpalen} · cap {m.cap_uren} uur × {fmtEuro(m.uurtarief)} = {fmtEuro(m.budget_bedrag)}
              </div>
            </>
          ) : (
            <Badge tone="gray">
              <Archive className="w-3 h-3" /> {p.totaal.afgerond}/{p.totaal.mijlpalen} mijlpalen gearchiveerd
            </Badge>
          )}
        </td>
        {/* 3. Uren-teller */}
        <td className={tdClass}>{m ? <Budgetmeter m={m} onClick={() => onLogs(m.id)} /> : <span className="text-xs text-gray-500">{fmtUren(p.totaal.geschreven_uren)} totaal</span>}</td>
        {/* 4. Financieel verbruik */}
        <td className={tdClass}>
          <div className="font-semibold">{fmtEuro(m ? m.verbruik_bedrag : p.totaal.verbruik_bedrag)}</div>
          <div className="text-[11px] text-gray-500">{m ? `op basis van ${m.geschreven_uren} u geschreven (max ${fmtEuro(m.budget_bedrag)})` : `geaccordeerd: ${fmtEuro(p.totaal.gefactureerd_bedrag)}`}</div>
        </td>
        {/* 5. State-machine status */}
        <td className={tdClass}>
          {st && m ? (
            <>
              <Badge tone={st.tone} mono>
                {m.status}
              </Badge>
              <div className="text-[11px] text-gray-500 mt-1">{st.label}</div>
            </>
          ) : (
            <Badge tone="gray" mono>
              {p.status}
            </Badge>
          )}
        </td>
        {/* 6. Budget-Grendel */}
        <td className={tdClass}>
          {grendel && m ? (
            <div className={`flex items-start gap-1 text-[11px] p-2 rounded-md ${TONE[grendel.tone]}`}>
              <grendel.icon className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
              <span>{grendel.tekst(m)}</span>
            </div>
          ) : (
            <span className="text-xs text-gray-400">Binnen budget</span>
          )}
        </td>
        {/* 7. Contextuele actieknop */}
        <td className={tdClass}>
          <ActieKnop project={p} />
        </td>
      </tr>
      {open && (
        <tr className="bg-gray-50/60">
          <td colSpan={7} className="px-6 py-3">
            <div className="text-xs font-semibold text-gray-600 uppercase mb-2">Alle mijlpalen</div>
            <ol className="space-y-1">
              {p.mijlpalen.map((x) => {
                const xs = MILESTONE_STATUS[x.status];
                const Icon = xs.icon;
                return (
                  <li key={x.id} className="grid grid-cols-1 md:grid-cols-[auto_1fr_200px_140px_auto] items-center gap-2 text-sm">
                    <Badge tone={xs.tone}>
                      <Icon className="w-3 h-3" /> {x.volgnummer}
                    </Badge>
                    <span>
                      {x.titel}
                      {x.extra_uren_toegekend > 0 && <span className="text-[11px] text-orange-700 ml-1">(+{x.extra_uren_toegekend} u extra)</span>}
                    </span>
                    <span className="font-mono text-xs">
                      {x.geschreven_uren} / {x.cap_uren} uur · {fmtEuro(x.verbruik_bedrag)}
                    </span>
                    <span className="text-xs text-gray-500">{x.status === 'ARCHIVED' ? `fiat ${fmtDateTime(x.approved_at)}` : xs.label}</span>
                    <button type="button" className="text-xs text-blue-700 hover:underline" onClick={() => onLogs(x.id)}>
                      uren-logs
                    </button>
                  </li>
                );
              })}
            </ol>
            {p.mijlpalen.some((x) => x.status === 'ARCHIVED' && x.approved_ip) && (
              <div className="text-[10px] text-gray-400 mt-2">Fiat-bewijs (tijdstip + IP) staat op elke gearchiveerde mijlpaal en wordt op de factuur afgedrukt.</div>
            )}
          </td>
        </tr>
      )}
    </>
  );
};

// ─── Grid ─────────────────────────────────────────────────────────────────────

export const B2bProjectGrid: React.FC = () => {
  const [wizard, setWizard] = useState(false);
  const [logsVan, setLogsVan] = useState<string | null>(null);
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: QK_PROJECTEN, queryFn: getB2bProjecten, refetchInterval: 60_000 });

  if (isLoading) {
    return (
      <div className="p-8 text-center text-gray-500">
        <Loader2 className="w-6 h-6 animate-spin inline" />
      </div>
    );
  }
  const regels = data?.regels ?? [];
  const pool = data?.pool ?? [];
  const t = data?.totaal;

  return (
    <div className="space-y-4">
      <div className="flex flex-col md:flex-row md:items-start gap-3">
        <div className="flex-1 flex items-start gap-2 p-3 rounded-lg bg-blue-50 border border-blue-200 text-sm text-blue-900">
          <ShieldCheck className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <span>
            <strong>Budget-Grendel:</strong> per mijlpaal geldt een harde uren-cap (cap × pool-uurtarief = verzegeld maximum). Boven de cap blokkeert de urenregistratie
            van de professional; u keurt extra uren goed of accordeert de afgeronde fase. Alleen de werkelijk geschreven uren gaan de SEPA-verzamelfactuur in (100% naar de professional, plus 15% platformfee),
            waarna de volgende mijlpaal automatisch van LOCKED_PENDING naar ACTIVE_RUNNING gaat.
          </span>
        </div>
        <Button onClick={() => setWizard((v) => !v)} disabled={pool.length === 0} title={pool.length === 0 ? 'Geen actieve pool-professionals' : undefined}>
          <Plus className="w-4 h-4 mr-1" /> Nieuw project
        </Button>
      </div>

      {t && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[
            ['Lopende projecten', String(t.actief)],
            ['Cap bereikt', String(t.cap_bereikt)],
            ['Wacht op fiat', String(t.wacht_op_fiat)],
            ['Financieel verbruik', fmtEuro(t.verbruik_bedrag)],
          ].map(([l, v], i) => (
            <div key={l} className={`p-3 rounded-lg border ${i === 1 && t.cap_bereikt > 0 ? 'bg-orange-50 border-orange-200' : i === 2 && t.wacht_op_fiat > 0 ? 'bg-green-50 border-green-200' : 'bg-gray-50 border-gray-200'}`}>
              <div className="text-xs text-gray-500">{l}</div>
              <div className="text-lg font-semibold text-gray-900">{v}</div>
            </div>
          ))}
        </div>
      )}

      {wizard && (
        <B2bProjectWizard
          pool={pool}
          onAnnuleren={() => setWizard(false)}
          onKlaar={() => {
            setWizard(false);
            qc.invalidateQueries({ queryKey: QK_PROJECTEN });
            qc.invalidateQueries({ queryKey: QK_OVERZICHT });
          }}
        />
      )}

      {regels.length === 0 ? (
        <div className="p-8 text-center text-gray-500 text-sm">
          Nog geen langlopende B2B-projecten. Start de Project-Onboarding Wizard om een project met mijlpalen en uren-caps op te zetten.
        </div>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-gray-200">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className={thClass}>Project &amp; Professional</th>
                <th className={thClass}>Actieve mijlpaal</th>
                <th className={thClass}>Uren-teller (budgetmeter)</th>
                <th className={thClass}>Financieel verbruik</th>
                <th className={thClass}>Status (state-machine)</th>
                <th className={thClass}>Budget-Grendel</th>
                <th className={thClass}>Actie</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {regels.map((p) => (
                <ProjectRij key={p.id} p={p} onLogs={setLogsVan} />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {logsVan && <UrenSlideOver milestoneId={logsVan} onSluit={() => setLogsVan(null)} />}
    </div>
  );
};

export default B2bProjectGrid;
